import { i18n } from "@/shared/i18n"

import type {
  BaseQueryApi,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue
} from "@reduxjs/toolkit/query"

type MaybePromise<T> = T | PromiseLike<T>

type BaseQueryFn = (
  arg: string | FetchArgs
) => MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>>

type QueryFnResult<Result> = QueryReturnValue<Result, FetchBaseQueryError, FetchBaseQueryMeta | undefined>

type QueryFnWithChatId<Arg, Result> = (
  arg: Arg,
  chatId: number,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>,
  baseQuery: BaseQueryFn
) => MaybePromise<QueryFnResult<Result>>

const buildMissingChatIdError = (): QueryFnResult<never> => ({
  error: {
    status: "CUSTOM_ERROR" as const,
    error: i18n.t("translations:common-error-missing-chat", "Unable to identify the chat. Please try again.")
  }
})

/**
 * Wraps a queryFn so that chatId is extracted from the mutation arg, validated centrally,
 * and forwarded as a separate (non-nullable) parameter to the callback alongside the remainder of the arg.
 *
 * The arg shape `{ chatId: number | null, ... }` mirrors what `useAppContext` exposes, so call sites
 * can forward the value verbatim — the helper turns a missing chatId into a translated CUSTOM_ERROR.
 */
export const queryFunctionWithChatId = <Arg extends { chatId: number | null }, Result>(
  callback: QueryFnWithChatId<Omit<Arg, "chatId">, Result>
): ((
  arg: Arg,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>,
  baseQuery: BaseQueryFn
) => MaybePromise<QueryFnResult<Result>>) => {
  return (arg, api, extraOptions, baseQuery) => {
    const { chatId, ...rest } = arg
    if (!chatId || Number.isNaN(chatId)) return buildMissingChatIdError()
    return callback(rest, chatId, api, extraOptions, baseQuery)
  }
}

interface ChatIdMutationOptions<Result> {
  transformResponse?: (data: unknown) => Result
  transformErrorResponse?: (error: FetchBaseQueryError) => FetchBaseQueryError
}

/**
 * Shorthand for mutation endpoints that need chatId. Reads chatId from the mutation arg,
 * handles baseQuery call and error check. Returns queryFn directly (no serializeQueryArgs needed).
 */
export const chatIdMutation = <Arg extends { chatId: number | null }, Result>(
  buildRequest: (chatId: number, arg: Omit<Arg, "chatId">) => FetchArgs,
  options?: ChatIdMutationOptions<Result>
) =>
  queryFunctionWithChatId<Arg, Result>(async (arg, chatId, _api, _extra, baseQuery) => {
    const result = await baseQuery(buildRequest(chatId, arg))

    if (result.error) {
      const error = options?.transformErrorResponse ? options.transformErrorResponse(result.error) : result.error
      return { error }
    }

    const data = options?.transformResponse ? options.transformResponse(result.data) : (result.data as Result)

    return { data }
  })
